import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Container, 
  Chip,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
  Slide
} from '@mui/material';
import { 
  ArrowForward as ArrowIcon,
  Star as StarIcon,
  Translate as TranslateIcon,
  VolumeUp as VolumeIcon,
  Mic as MicIcon,
  Chat as ChatIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingIcon,
  Public as GlobalIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import imghome from '../assests/wall.jpg';
import logoImg from '../assests/logo.jpg';
import ProductCard from "../components/ProductCard";
import ProductData from "../Constants/ProductData";

const Products = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredProducts, setFilteredProducts] = useState(ProductData);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const servicesRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProducts(ProductData);
    } else {
      setFilteredProducts(ProductData.filter(product => 
        product.title.toLowerCase().includes(activeFilter.toLowerCase())
      ));
    }
  }, [activeFilter]);

  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const categories = [
    { key: 'all', label: 'All Products', icon: <StarIcon />, color: '#F59E0B' },
    { key: 'avoice', label: 'Avoices', icon: <TranslateIcon />, color: '#F59E0B' },
    { key: 'agents', label: 'AI Agents', icon: <ChatIcon />, color: '#F59E0B' },
    { key: 'voice', label: 'Voice AI', icon: <VolumeIcon />, color: '#F59E0B' },
    { key: 'agriculture', label: 'Agriculture', icon: <BusinessIcon />, color: '#F59E0B' },
    { key: 'analytics', label: 'Analytics', icon: <AnalyticsIcon />, color: '#F59E0B' }
  ];



  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box sx={{
        minHeight: '70vh',
        backgroundColor: '#f8fafc',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}>
        {/* Clean Corporate Grid Background */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          backgroundImage: `radial-gradient(#9CA3AF 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          zIndex: 0,
        }} />

        {/* Transparent Logo Background Left */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Transparent Logo Background Right */}
        <Box sx={{
          position: 'absolute',
          top: '50%',
          right: '-10%',
          transform: 'translateY(-50%)',
          height: '90vh',
          width: '50vw',
          backgroundImage: `url(${logoImg})`,
          backgroundSize: 'contain',
          backgroundPosition: 'right center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.07,
          zIndex: 1,
          mixBlendMode: 'multiply'
        }} />

        {/* Premium Blur Glows */}
        <Box sx={{
          position: 'absolute',
          top: '-20%',
          left: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: '-20%',
          right: '10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(circle, rgba(245,158,11, 0.04) 0%, transparent 60%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center" sx={{ minHeight: '70vh' }}>
            <Grid item xs={12} lg={12}>
              <Fade in={isVisible} timeout={1000}>
                <Box sx={{ color: '#111827', textAlign: 'center' }}>
                  {/* Badge */}
                  <Chip
                    icon={<StarIcon />}
                    label="Our Products"
                    sx={{
                      backgroundColor: '#F3F4F6',
                      color: '#4B5563',
                      border: '1px solid #E5E7EB',
                      fontWeight: 700,
                      mb: 3,
                      px: 2,
                      py: 1,
                      borderRadius: '20px',
                      '& .MuiChip-icon': { color: '#F59E0B' }
                    }}
                  />

                  {/* Main Title */}
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '2.8rem', sm: '3.8rem', md: '4.5rem', lg: '5.5rem' },
                      lineHeight: 1.05,
                      letterSpacing: '-0.04em',
                      mb: 2.5,
                      color: '#111827',
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    PHOSAI PRODUCTS
                  </Typography>

                  {/* Subtitle */}
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.2rem', sm: '1.45rem', md: '1.75rem' },
                      lineHeight: 1.35,
                      color: '#4B5563',
                      mb: 4,
                      maxWidth: 600,
                      mx: 'auto',
                    }}
                  >
                    World-Class AI Products for African Languages & Business
                  </Typography>

                  {/* Description */}
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      color: '#6B7280',
                      mb: 5,
                      maxWidth: 700,
                      mx: 'auto',
                      fontWeight: 500,
                    }}
                  >
                    Discover our comprehensive suite of AI-powered products designed specifically 
                    for African languages, business automation, and cultural contexts.
                  </Typography>

                  {/* CTA Button */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToServices}
                    endIcon={<ArrowIcon />}
                    sx={{
                      background: '#F59E0B',
                      color: 'white',
                      px: 5,
                      py: 2,
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 14px rgba(245,158,11, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(245,158,11, 0.4)',
                        background: '#D97706',
                      }
                    }}
                  >
                    Explore Products
                  </Button>
                </Box>
              </Fade>
            </Grid>


          </Grid>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Box sx={{
        py: { xs: 8, md: 12, lg: 16 },
        background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          {/* Products Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Chip
                  label="OUR PRODUCTS"
                  sx={{
                    backgroundColor: 'rgba(245,158,11, 0.1)',
                    color: '#F59E0B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: 3,
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                />
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                      mb: 4,
                    color: '#111827',
                  }}
                >
                  Comprehensive AI Products
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: '#6B7280',
                    maxWidth: 700,
                    margin: '0 auto',
                    fontWeight: 500,
                  }}
                >
                  From language processing to business automation, we deliver cutting-edge products 
                  that bridge linguistic barriers and drive business growth across Africa
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Category Filters */}
          <Box sx={{ mb: { xs: 6, md: 8 } }}>
            <Fade in={isVisible} timeout={1000}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                flexWrap: 'wrap', 
                gap: 2 
              }}>
                {categories.map((category) => (
                  <Chip
                    key={category.key}
                    icon={category.icon}
                    label={category.label}
                    onClick={() => setActiveFilter(category.key)}
                    sx={{
                      backgroundColor: activeFilter === category.key 
                        ? category.color 
                        : 'rgba(245,158,11, 0.1)',
                      color: activeFilter === category.key 
                        ? 'white' 
                        : '#F59E0B',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      px: 3,
                      py: 2,
                      borderRadius: '25px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: `2px solid ${activeFilter === category.key ? category.color : 'rgba(245,158,11, 0.2)'}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${category.color}30`,
                        backgroundColor: activeFilter === category.key 
                          ? category.color 
                          : 'rgba(245,158,11, 0.15)',
                      },
                      '& .MuiChip-icon': { 
                        color: activeFilter === category.key ? 'white' : category.color 
                      }
                    }}
                  />
                ))}
              </Box>
            </Fade>
          </Box>

          {/* Products Grid */}
          <Box ref={servicesRef}>
            <Grid container spacing={4}>
              {filteredProducts.map((product, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <Fade in={isVisible} timeout={800 + index * 200}>
                    <Box
                      sx={{
                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                        '@keyframes fadeInUp': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateY(30px)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateY(0)',
                          },
                        },
                      }}
                    >
                      <ProductCard
                        key={product.title}
                        img={product.img}
                        title={product.title}
                        description={product.description}
                        benefits={product.benefits}
                        useCases={product.useCases}
                        link={product.link}
                      />
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Call to Action */}
          <Box sx={{ 
            textAlign: 'center',
            mt: { xs: 8, md: 12 },
            p: { xs: 6, md: 8 },
            borderRadius: '30px',
            background: 'linear-gradient(135deg, rgba(245,158,11, 0.08) 0%, rgba(124, 58, 237, 0.04) 100%)',
            border: '1px solid rgba(245,158,11, 0.15)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Fade in={isVisible} timeout={1200}>
              <Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    mb: 3,
                    color: '#111827',
                  }}
                >
                  Ready to Transform Your Business with AI?
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem',
                    color: '#6B7280',
                    mb: 4,
                    maxWidth: 600,
                    margin: '0 auto',
                  }}
                >
                  Let's discuss how PHOSAI products can help you automate processes, bridge language barriers, and unlock new business opportunities.
                </Typography>
                <Button 
                  variant="contained" 
                  size="large"
                  startIcon={<PlayIcon />}
                  sx={{ 
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white',
                    px: 6,
                    py: 2.5,
                    borderRadius: '20px',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: '0 15px 35px rgba(245,158,11, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 25px 50px rgba(245,158,11, 0.4)',
                      background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                    }
                  }}
                                  >
                    Explore Products
                  </Button>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Chip
                  label="READY TO START?"
                  sx={{
                    backgroundColor: 'rgba(245,158,11, 0.2)',
                    color: '#F59E0B',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    mb: 3,
                    px: 3,
                    py: 1.5,
                    borderRadius: '20px',
                  }}
                />
                
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    mb: 4,
                    color: 'white',
                  }}
                >
                  Transform Your Business with AI
                </Typography>
                
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: '#D1D5DB',
                    maxWidth: 700,
                    margin: '0 auto',
                    fontWeight: 500,
                    mb: 6,
                  }}
                >
                  See our AI products in action. Schedule a personalized demo and discover how PHOSAI 
                  can solve your specific business challenges.
                </Typography>

                {/* Contact Methods */}
                <Grid container spacing={4} sx={{ maxWidth: 800, margin: '0 auto' }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          mb: 2,
                        }}
                      >
                        +256 750 371 313
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        +256 702 560 814
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                          wordBreak: 'break-word',
                        }}
                      >
                        phosaico@gmail.com
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: 'rgba(245,158,11, 0.1)',
                      border: '1px solid rgba(245,158,11, 0.2)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(245,158,11, 0.15)',
                      }
                    }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: '#F59E0B',
                          mb: 2,
                        }}
                      >
                        Location
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#D1D5DB',
                        }}
                      >
                        Makerere University
                        <br />
                        Kampala, Uganda
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* CTA Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 3, 
                  flexWrap: 'wrap',
                  mt: 6 
                }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ArrowIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      color: 'white',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      boxShadow: '0 15px 35px rgba(245,158,11, 0.4)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 25px 50px rgba(245,158,11, 0.5)',
                        background: 'linear-gradient(135deg, #D97706 0%, #6D28D9 100%)',
                      }
                    }}
                    onClick={() => window.open('mailto:phosaico@gmail.com?subject=Schedule Demo - PHOSAI Products', '_blank')}
                  >
                    Schedule Demo
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      color: 'white',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      px: 5,
                      py: 2.5,
                      borderRadius: '20px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 15px 35px rgba(255, 255, 255, 0.2)',
                      }
                    }}
                    onClick={() => window.open('tel:+256750371313', '_blank')}
                  >
                    Call Now
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Products;
